import { useEffect, useState } from "react";
import { ArrowUpRight, Printer, Pencil, Save, X, Trash2, ArrowLeft } from "lucide-react";
import { api, money, moneyCur, humanDate, GOLD_TYPES, canEditWorkOrder, openInvoiceFromWorkOrders, rowLink, goBack } from "../lib/api";
import { MoneyInput, HideDailyToggle, PhotoLightbox } from "../lib/ui";
import { invoiceGroups, CategorySelect, CustomerInfoButton, StockCombobox, ServiceCombobox, CustomerCombobox } from "./Modals";
import { WorkOrderForm } from "./WorkPanel";

function Row({ label, children, testid }) {
  return <div><span>{label}</span><strong data-testid={testid}>{children}</strong></div>;
}

const KDV_OPTS = [0, 1, 8, 10, 18, 20];

/* --------------------------- Inline edit forms --------------------------- */
function InputField({ label, value, onChange, type = "text", step, testid, disabled, money: isMoney }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {isMoney ? (
        <MoneyInput value={value} testid={testid} disabled={disabled}
                    onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} step={step} value={value ?? ""} disabled={disabled}
               data-testid={testid}
               onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <select value={value ?? ""} data-testid={testid} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  );
}

const catalogCache = {};
function useCatalog(kind) {
  const [list, setList] = useState(catalogCache[kind] || []);
  useEffect(() => {
    if (catalogCache[kind]) return;
    api.get(kind === "service" ? "/services" : "/stocks").then((r) => { catalogCache[kind] = r.data; setList(r.data); }).catch(() => {});
  }, [kind]);
  return list;
}

function LineEditor({ rows, onChange, label, withCode = true }) {
  const kind = withCode ? "product" : "service";
  const catalog = useCatalog(kind);
  const update = (i, patch) => onChange(rows.map((r, j) => j === i ? { ...r, ...patch } : r));
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));
  const addRow = () => onChange([...rows, { name: "", code: "", description: "", quantity: 1, unit_price: 0, kdv: 20 }]);
  const pick = (i, s) => update(i, kind === "product"
    ? { stock_id: s.id, code: s.code, name: s.name, unit: s.unit, unit_price: s.price ?? 0, kdv: s.kdv ?? 20 }
    : { service_id: s.id, code: s.code, name: s.name, unit_price: s.price ?? 0, kdv: s.kdv ?? 20 });
  return (
    <div className="edit-lines">
      <div className="edit-lines-head">
        <strong>{label}</strong>
        <button type="button" className="secondary-button" onClick={addRow}
                data-testid={`edit-line-add-${label}`}>+ Satır</button>
      </div>
      {rows.length === 0 && <div className="line-empty">Satır yok.</div>}
      {rows.map((row, i) => {
        const selectedLabel = row.name ? (row.code ? `${row.code} · ${row.name}` : row.name) : "";
        return (
        <div key={i} className="edit-line-row" data-testid={`edit-line-${label}-${i}`}>
          {kind === "product"
            ? <StockCombobox stocks={catalog} selectedId={row.stock_id} selectedLabel={selectedLabel} index={`edit-${i}`} onSelect={(s) => pick(i, s)} />
            : <ServiceCombobox services={catalog} selectedLabel={selectedLabel} index={`edit-${i}`} onSelect={(s) => pick(i, s)} />}
          <input placeholder="Açıklama" value={row.description || ""}
                 onChange={(e) => update(i, { description: e.target.value })} />
          <input type="number" step="0.01" placeholder="Mik" value={row.quantity ?? row.qty ?? 0}
                 onChange={(e) => update(i, { quantity: e.target.value, qty: e.target.value })} />
          <MoneyInput placeholder="Birim" value={row.unit_price ?? 0}
                      onChange={(e) => update(i, { unit_price: e.target.value })} />
          <select value={row.kdv ?? 20} onChange={(e) => update(i, { kdv: e.target.value })}>
            {KDV_OPTS.map((k) => <option key={k} value={k}>%{k}</option>)}
          </select>
          <button type="button" className="row-icon tone-danger" onClick={() => removeRow(i)}>
            <Trash2 size={13} />
          </button>
        </div>
        );
      })}
    </div>
  );
}

/* --------------------------- Page ----------------------------- */
export default function DetailPage() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const kind = parts[0] === "is-fisi" ? "is-fisi" : parts[1];
  const id = parts[0] === "is-fisi" ? parts[1] : parts[2];
  const [state, setState] = useState({ loading: true, error: "", data: null, customers: [], accounts: [], extra: {}, user: null });
  const [edit, setEdit] = useState(false);
  const [woStocks, setWoStocks] = useState(null);
  const [form, setForm] = useState({});
  const [saveMsg, setSaveMsg] = useState("");

  const refresh = async () => {
    const [{ data: customers }, { data: accounts }, me] = await Promise.all([
      api.get("/customers?archived=all"), api.get("/accounts").catch(() => ({ data: [] })), api.get("/auth/me").catch(() => ({ data: null })),
    ]);
    let data = null;
    const extra = {};
    if (kind === "fatura") {
      const { data: invs } = await api.get("/invoices?scope=all");
      data = invs.find((x) => x.id === id);
      if (!data) throw new Error("Fatura bulunamadı");
    } else if (kind === "hareket") {
      const { data: txs } = await api.get("/transactions");
      data = txs.find((x) => x.id === id);
      if (!data) throw new Error("Hareket bulunamadı");
      if (data.proxy_link_id) extra.pair = txs.find((t) => t.proxy_link_id === data.proxy_link_id && t.id !== data.id) || null;
      const invId = data.invoice_id || data.related_invoice_id;
      if (invId) {
        const { data: invs } = await api.get("/invoices?scope=all");
        extra.invoice = invs.find((x) => x.id === invId) || null;
      }
    } else if (kind === "cek") {
      const { data: cheques } = await api.get("/cheques");
      data = cheques.find((x) => x.id === id);
      if (!data) throw new Error("Çek bulunamadı");
    } else if (kind === "alim") {
      const { data: pis } = await api.get("/purchase-invoices?scope=all");
      data = pis.find((x) => x.id === id);
      if (!data) throw new Error("Alım faturası bulunamadı");
    } else if (kind === "is-fisi") {
      const { data: wo } = await api.get(`/work-orders/${id}`);
      data = wo;
    } else if (kind === "kayit") {
      const { data: all } = await api.get("/records");
      data = all.find((x) => x.id === id);
      if (!data) throw new Error("Kayıt bulunamadı");
    } else if (kind === "stok") {
      const { data: all } = await api.get("/stocks");
      data = all.find((x) => x.id === id);
      if (!data) throw new Error("Stok bulunamadı");
    } else if (kind === "hizmet") {
      const { data: all } = await api.get("/services");
      data = all.find((x) => x.id === id);
      if (!data) throw new Error("Hizmet bulunamadı");
    } else if (kind === "hesap") {
      data = accounts.find((x) => x.id === id);
      if (!data) throw new Error("Hesap bulunamadı");
    } else {
      throw new Error("Geçersiz detay türü");
    }
    setState({ loading: false, error: "", data, customers, accounts, extra, user: me?.data || null });
    setForm(deepCloneForEdit(kind, data));
  };

  useEffect(() => {
    (async () => {
      try { await refresh(); }
      catch (err) { setState((s) => ({ ...s, loading: false, error: err.response?.data?.detail || err.message || "Kayıt bulunamadı" })); }
    })();
  }, [kind, id]); // eslint-disable-line
  useEffect(() => {
    const h = () => refresh().catch(() => {});
    window.addEventListener("sp-customer-updated", h);
    return () => window.removeEventListener("sp-customer-updated", h);
  }, [kind, id]); // eslint-disable-line

  const { loading, error, data, customers, accounts, extra, user } = state;
  const isAdmin = user?.role === "admin";
  const custName = (cid) => customers.find((c) => c.id === cid)?.name || "—";

  const startEdit = () => { setForm(deepCloneForEdit(kind, data)); setEdit(true); setSaveMsg(""); };
  const cancelEdit = () => { setForm(deepCloneForEdit(kind, data)); setEdit(false); setSaveMsg(""); };

  const save = async () => {
    try {
      setSaveMsg("");
      const payload = prepareSavePayload(kind, form);
      let path = "";
      if (kind === "fatura") path = `/invoices/${id}`;
      else if (kind === "hareket") path = `/transactions/${id}`;
      else if (kind === "cek") path = `/cheques/${id}`;
      else if (kind === "alim") path = `/purchase-invoices/${id}`;
      else if (kind === "is-fisi") path = `/work-orders/${id}`;
      else if (kind === "kayit") path = `/records/${id}`;
      else if (kind === "stok") path = `/stocks/${id}`;
      else if (kind === "hizmet") path = `/services/${id}`;
      else if (kind === "hesap") path = `/accounts/${id}`;
      await api.put(path, payload);
      setEdit(false);
      setSaveMsg("Kaydedildi.");
      await refresh();
    } catch (ex) {
      setSaveMsg(ex.response?.data?.detail || "Kaydedilemedi.");
    }
  };

  const EditToggle = () => (
    !isAdmin || kind === "is-fisi" ? null : (edit ? (
      <>
        <button type="button" className="secondary-button" onClick={cancelEdit} data-testid="detail-edit-cancel">
          <X size={14} /> İptal
        </button>
        <button type="button" className="primary-button" onClick={save} data-testid="detail-edit-save">
          <Save size={14} /> Kaydet
        </button>
      </>
    ) : (
      <button type="button" className="primary-button" onClick={startEdit} data-testid="detail-edit-btn">
        <Pencil size={14} /> Düzenle
      </button>
    ))
  );

  return (
    <div className="detail-page">
      <div className="detail-card" data-testid="detail-page">
        <div className="detail-head">
          <button data-testid="detail-back-button" type="button" className="secondary-button" onClick={goBack} title="Önceki sayfaya dön">
            <ArrowLeft size={14} /> Geri
          </button>
          <img src="/logo-serhan.png" alt="Serhan Elektronik" className="brand-logo" />
          <span className="brand-caption">ELEKTRONİK MUHASEBE</span>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            {!edit && <button data-testid="detail-print-button" type="button" className="secondary-button" onClick={() => window.print()}>
              <Printer size={14} /> Yazdır
            </button>}
            <EditToggle />
          </div>
        </div>

        {saveMsg && <div className={saveMsg.includes("Kaydedildi") ? "form-success" : "form-error"} data-testid="detail-save-msg">{saveMsg}</div>}

        {loading && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Yükleniyor…</div>}
        {error && <div className="form-error" data-testid="detail-error">{error}</div>}

        {!loading && !error && data && kind === "fatura" && (
          !edit ? <InvoiceView data={data} customers={customers} /> :
          <InvoiceEdit form={form} setForm={setForm} customers={customers} />
        )}

        {!loading && !error && data && kind === "hareket" && (
          !edit ? <TxView data={data} customers={customers} accounts={accounts} extra={extra} /> :
          <TxEdit form={form} setForm={setForm} customers={customers} accounts={accounts} />
        )}

        {!loading && !error && data && kind === "cek" && (
          !edit ? <ChequeView data={data} customers={customers} /> :
          <ChequeEdit form={form} setForm={setForm} customers={customers} />
        )}

        {!loading && !error && data && kind === "alim" && (
          !edit ? <PurchaseView data={data} accounts={accounts} /> :
          <PurchaseEdit form={form} setForm={setForm} customers={customers} accounts={accounts} />
        )}

        {!loading && !error && data && kind === "kayit" && (
          !edit ? <RecordView data={data} customers={customers} accounts={accounts} /> :
          <RecordEdit form={form} setForm={setForm} customers={customers} accounts={accounts} />
        )}

        {!loading && !error && data && kind === "stok" && (
          !edit ? <StockView data={data} /> : <StockEdit form={form} setForm={setForm} />
        )}

        {!loading && !error && data && kind === "hizmet" && (
          !edit ? <ServiceView data={data} /> : <ServiceEdit form={form} setForm={setForm} />
        )}

        {!loading && !error && data && kind === "hesap" && (
          !edit ? <AccountView data={data} /> : <AccountEdit form={form} setForm={setForm} />
        )}

        {!loading && !error && data && kind === "is-fisi" && (
          !edit ? <>
            {canEditWorkOrder(user, data) && (
              <div className="button-row" style={{ justifyContent: "flex-end" }}>
                <button type="button" className="primary-button" data-testid="wo-detail-edit"
                        onClick={async () => { if (!woStocks) setWoStocks((await api.get("/stocks").catch(() => ({ data: [] }))).data); setEdit(true); setSaveMsg(""); }}>
                  <Pencil size={14} /> Fişi düzenle
                </button>
              </div>
            )}
            {data.status === "işlendi" && <p className="muted-note" data-testid="wo-processed-note">Bu fiş muhasebe tarafından işlendi; yapılan değişiklikler muhasebeye bildirilir.</p>}
            <WorkOrderView data={data} custName={custName} user={user} onChanged={refresh}
                           canInvoice={isAdmin || (!!user?.permissions?.access_accounting && !user?.work_only)} />
          </> : (
            <WorkOrderForm editId={id} stocks={woStocks || []} onCancel={() => setEdit(false)}
                           initial={{ customer_name: data.customer_name || "", customer_id: data.customer_id || null, plate: data.plate || "",
                                      chassis_no: data.chassis_no || "", job: data.job || "", note: data.note || "",
                                      vehicle_make: data.vehicle_make || "", vehicle_model: data.vehicle_model || "", vehicle_year: data.vehicle_year || "",
                                      ruhsat_photo: data.ruhsat_photo || null,
                                      no_vehicle: !!data.no_vehicle, owner_id_no: data.owner_id_no || "", owner_address: data.owner_address || "",
                                      items: (data.items || []).length ? data.items : [{ is_product: false, stock_id: null, code: "", name: "", qty: 1, unit_price: 0, description: "" }] }}
                           onSaved={async () => { setEdit(false); setSaveMsg("Kaydedildi."); await refresh(); }} />
          )
        )}

        {!loading && !error && data && (
          <AuditTimeline key={saveMsg} entity={entityForKind(kind)} entityId={id} />
        )}
      </div>
    </div>
  );
}

/* --------------------- Audit log timeline --------------------- */
const KIND_TO_ENTITY = {
  "fatura": "invoice", "hareket": "transaction", "cek": "cheque",
  "alim": "purchase_invoice", "is-fisi": "work_order", "kayit": "record",
  "stok": "stock", "hizmet": "service", "hesap": "account",
};
function entityForKind(k) { return KIND_TO_ENTITY[k] || k; }

function AuditTimeline({ entity, entityId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/activity/entity/${entity}/${entityId}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [entity, entityId]);
  const label = (a) => a === "create" ? "Oluşturdu" : a === "update" ? "Güncelledi" : a === "delete" ? "Sildi" : a === "convert" ? "Dönüştürdü" : a === "transfer" ? "Transfer" : a === "export" ? "Dışa aktardı" : a;
  const tone = (a) => a === "create" ? "positive" : a === "delete" ? "negative" : "";
  return (
    <div className="audit-timeline" data-testid="audit-timeline">
      <h4>Düzenleme günlüğü</h4>
      {loading && <div className="audit-empty">Yükleniyor…</div>}
      {!loading && items.length === 0 && <div className="audit-empty">Henüz bu kayıt için günlük yok.</div>}
      {!loading && items.length > 0 && (
        <ul className="audit-list">
          {items.map((it) => (
            <li key={it.id} className={`audit-item audit-${it.action}`}>
              <div className="audit-dot" aria-hidden />
              <div className="audit-body">
                <div className="audit-line">
                  <strong>{it.user_name || "Sistem"}</strong>
                  <span className={`audit-action tone-${tone(it.action)}`}>{label(it.action)}</span>
                  <small>{humanDate(it.created_at)} · {(it.created_at || "").slice(11, 16)}</small>
                </div>
                {it.description && <div className="audit-desc">{it.description}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* --------------------- clones & payload builders --------------------- */
function deepCloneForEdit(kind, d) {
  if (!d) return {};
  const c = JSON.parse(JSON.stringify(d));
  if (kind === "fatura" || kind === "alim") {
    c.products = c.products || c.items?.filter?.((x) => !x.is_service) || [];
    c.services = c.services || c.items?.filter?.((x) => x.is_service) || [];
  }
  return c;
}
function prepareSavePayload(kind, f) {
  const p = { ...f };
  const named = (rows) => (rows || []).filter((r) => String(r.name || "").trim());
  if (kind === "fatura" || kind === "alim") { p.products = named(p.products); p.services = named(p.services); }
  if (kind === "fatura") {
    p.subtotal = (p.products || []).reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0), 0)
               + (p.services || []).reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0), 0);
    p.kdv_total = (p.products || []).reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0) * (Number(r.kdv) || 0) / 100, 0)
                + (p.services || []).reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0) * (Number(r.kdv) || 0) / 100, 0);
    p.total = p.subtotal + p.kdv_total;
    p.products = p.products.map((r) => ({ ...r, quantity: Number(r.quantity) || 0, unit_price: Number(r.unit_price) || 0, kdv: Number(r.kdv) || 0 }));
    p.services = p.services.map((r) => ({ ...r, quantity: Number(r.quantity) || 0, unit_price: Number(r.unit_price) || 0, kdv: Number(r.kdv) || 0 }));
  } else if (kind === "alim") {
    const items = [
      ...(p.products || []).map((r) => ({ is_service: false, name: r.name, code: r.code, description: r.description, stock_id: r.stock_id || null,
        qty: Number(r.quantity ?? r.qty) || 0, unit_price: Number(r.unit_price) || 0, kdv: Number(r.kdv) || 0 })),
      ...(p.services || []).map((r) => ({ is_service: true, name: r.name, code: r.code, description: r.description, service_id: r.service_id || null,
        qty: Number(r.quantity ?? r.qty) || 0, unit_price: Number(r.unit_price) || 0, kdv: Number(r.kdv) || 0 })),
    ];
    p.items = items;
    p.subtotal = items.reduce((s, r) => s + r.qty * r.unit_price, 0);
    p.kdv_total = items.reduce((s, r) => s + r.qty * r.unit_price * r.kdv / 100, 0);
    p.total = p.subtotal + p.kdv_total;
    delete p.products; delete p.services;
  }
  return p;
}

/* --------------------- view/edit components --------------------- */
function InvoiceView({ data }) {
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">{data.invoice_type || "Resmi"} Fatura · {data.invoice_no}</h1>
        <span className={`type-tag ${data.invoice_type === "Proforma" ? "individual" : "corporate"}`}>{data.status}</span>
      </div>
      <div className="view-grid">
        <Row label="Cari" testid="detail-customer">{data.customer_name} <CustomerInfoButton customerId={data.customer_id} testid="detail-customer-info" /></Row>
        <Row label="Tarih">{humanDate(data.date)}</Row>
        <Row label="Vade">{humanDate(data.due_date)}</Row>
        {(data.vehicles || []).length <= 1 && data.plate && <Row label="Plaka">{data.plate}</Row>}
        {(data.vehicles || []).length <= 1 && data.chassis_no && <Row label="Şase">{data.chassis_no}</Row>}
      </div>
      {invoiceGroups(data).map((g, gi) => (
        <div key={gi} className={g.title ? "vehicle-view-block" : ""} data-testid={g.title ? `detail-vehicle-${gi}` : undefined}>
          {g.title && <h4 className="vehicle-view-title">{g.title}</h4>}
          <ItemsTable products={g.products} services={g.services} />
        </div>
      ))}
      <div className="invoice-totals" style={{ marginTop: 18 }}>
        <div><span>Ara toplam</span><strong>{money(data.subtotal)}</strong></div>
        <div><span>KDV</span><strong>{money(data.kdv_total)}</strong></div>
        <div className="grand"><span>Genel toplam</span><strong data-testid="detail-total">{money(data.total)}</strong></div>
      </div>
      {data.note && <p className="modal-sub" style={{ marginTop: 14 }}><strong>Not:</strong> {data.note}</p>}
    </>
  );
}
function ItemsTable({ products = [], services = [] }) {
  const rows = [...products.map((p) => ({ ...p, kind: "Ürün" })), ...services.map((s) => ({ ...s, kind: "Hizmet" }))];
  if (!rows.length) return null;
  return (
    <table className="view-table" style={{ marginTop: 18 }}>
      <thead>
        <tr><th>Kalem</th><th>Miktar</th><th className="amount">Birim</th><th className="amount">KDV</th><th className="amount">Tutar</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td><strong>{r.name}</strong><small>{r.code} {r.description && `· ${r.description}`}</small>{r.extra_note && <small style={{ display: "block", whiteSpace: "pre-wrap" }}>{r.extra_note}</small>}</td>
            <td>{r.quantity}</td>
            <td className="amount">{money(r.unit_price)}</td>
            <td className="amount">%{r.kdv}</td>
            <td className="amount">{money((Number(r.quantity) || 0) * (Number(r.unit_price) || 0) * (1 + (Number(r.kdv) || 0) / 100))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function InvoiceEdit({ form, setForm, customers = [] }) {
  return (
    <div className="edit-form" data-testid="invoice-edit-form">
      <HideDailyToggle form={form} setForm={setForm} testid="invoice-edit-form-hide-daily" />
      <div className="form-grid two">
        <CustomerCombobox customers={customers} selectedId={form.customer_id} selectedName={form.customer_name}
                          label="Cari" testid="edit-customer"
                          onSelect={(c) => setForm({ ...form, customer_id: c.id, customer_name: c.name })} />
        <InputField label="Fatura no" value={form.invoice_no} onChange={(v) => setForm({ ...form, invoice_no: v })} testid="edit-invoice-no" />
        <InputField label="Tarih" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
        <InputField label="Vade" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
        <SelectField label="Durum" value={form.status} testid="edit-status"
                     onChange={(v) => setForm({ ...form, status: v })}
                     options={["Ödenecek", "Kısmen Ödendi", "Ödendi", "İptal"]} />
        <InputField label="Plaka" value={form.plate} onChange={(v) => setForm({ ...form, plate: v })} />
      </div>
      <LineEditor rows={form.products || []} label="Ürünler" onChange={(rows) => setForm({ ...form, products: rows })} />
      <LineEditor rows={form.services || []} label="Hizmetler" withCode={false} onChange={(rows) => setForm({ ...form, services: rows })} />
      <InputField label="Not" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
    </div>
  );
}

function TxView({ data, customers, accounts, extra }) {
  const custName = (cid) => customers.find((c) => c.id === cid)?.name || "—";
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">Kasa / Banka Hareketi</h1>
        <span className={`type-tag ${data.direction === "in" ? "corporate" : "individual"}`}>
          {data.direction === "in" ? "Giriş / Tahsilat" : "Çıkış / Ödeme"}
        </span>
      </div>
      <div className="view-grid">
        <Row label="Tarih">{humanDate(data.date)}</Row>
        <Row label="Tutar" testid="detail-amount">{data.direction === "in" ? "+" : "-"}{money(data.amount)}</Row>
        <Row label="Kategori">{data.category || "—"}</Row>
        <Row label="Hesap">{data.account_id ? (accounts.find((a) => a.id === data.account_id)?.name || "—") : "Cari Hesap üzerinden"}</Row>
        {data.customer_id && <Row label="Cari" testid="detail-customer">{custName(data.customer_id)} <CustomerInfoButton customerId={data.customer_id} testid="detail-customer-info" /></Row>}
      </div>
      {data.description && <p className="modal-sub" style={{ marginTop: 14 }}><strong>Açıklama:</strong> {data.description}</p>}
      {extra.pair && (
        <div className="modal-sub" style={{ marginTop: 12, padding: 12, background: "#eff6ff", borderRadius: 8, borderLeft: "3px solid #17a9d8" }} data-testid="detail-linked-pair">
          <strong>Cari Hesap bağlantılı hareketi:</strong> {custName(extra.pair.customer_id)} ·
          {extra.pair.direction === "in" ? " Giriş" : " Çıkış"} · {money(extra.pair.amount)}
          <a href={`/detay/hareket/${extra.pair.id}`} target="_blank" rel="noreferrer" style={{ marginLeft: 10, color: "#0e86b0", fontWeight: 700 }}>
            Bağlı hareketi aç <ArrowUpRight size={12} style={{ verticalAlign: -2 }} />
          </a>
        </div>
      )}
      {extra.invoice && (
        <div className="modal-actions" style={{ marginTop: 16 }}>
          <a data-testid="detail-open-invoice" className="primary-button" href={`/detay/fatura/${extra.invoice.id}`} target="_blank" rel="noreferrer">
            Fatura {extra.invoice.invoice_no} → aç <ArrowUpRight size={14} />
          </a>
        </div>
      )}
    </>
  );
}
function TxEdit({ form, setForm, accounts }) {
  return (
    <div className="edit-form" data-testid="tx-edit-form">
      <HideDailyToggle form={form} setForm={setForm} testid="tx-edit-form-hide-daily" />
      <div className="form-grid two">
        <InputField label="Tarih" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
        <InputField label="Tutar" money value={form.amount} testid="edit-tx-amount"
                    onChange={(v) => setForm({ ...form, amount: v })} />
        <SelectField label="Yön" value={form.direction}
                     onChange={(v) => setForm({ ...form, direction: v })}
                     options={[{ value: "in", label: "Giriş" }, { value: "out", label: "Çıkış" }]} />
        <InputField label="Kategori" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
        <SelectField label="Hesap" value={form.account_id || ""}
                     onChange={(v) => setForm({ ...form, account_id: v })}
                     options={[{ value: "", label: "—" }, ...accounts.map((a) => ({ value: a.id, label: a.name }))]} />
        <InputField label="Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="edit-tx-desc" />
      </div>
    </div>
  );
}

function ChequeView({ data, customers }) {
  const custName = (cid) => customers.find((c) => c.id === cid)?.name || "—";
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">{data.direction === "received" ? "Alınan" : "Verilen"} Çek · {data.check_no || "-"}</h1>
        <span className="type-tag">{data.status}</span>
      </div>
      <div className="view-grid">
        <Row label="Çek no">{data.check_no || "—"}</Row>
        <Row label="Banka / Şube">{[data.bank_name, data.branch].filter(Boolean).join(" / ") || "—"}</Row>
        <Row label="Veriliş">{humanDate(data.issue_date)}</Row>
        <Row label="Vade">{humanDate(data.maturity_date)}</Row>
        <Row label="Tutar" testid="detail-amount">{money(data.amount)}</Row>
        {data.customer_id && <Row label="Cari" testid="detail-customer">{custName(data.customer_id)} <CustomerInfoButton customerId={data.customer_id} testid="detail-customer-info" /></Row>}
      </div>
      {data.note && <p className="modal-sub" style={{ marginTop: 14 }}><strong>Not:</strong> {data.note}</p>}
    </>
  );
}
function ChequeEdit({ form, setForm, customers }) {
  return (
    <div className="edit-form" data-testid="cheque-edit-form">
      <div className="form-grid two">
        <InputField label="Çek no" value={form.check_no} onChange={(v) => setForm({ ...form, check_no: v })} testid="edit-cheque-no" />
        <InputField label="Banka" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
        <InputField label="Şube" value={form.branch} onChange={(v) => setForm({ ...form, branch: v })} />
        <InputField label="Veriliş" type="date" value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} />
        <InputField label="Vade" type="date" value={form.maturity_date} onChange={(v) => setForm({ ...form, maturity_date: v })} />
        <InputField label="Tutar" money value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="edit-cheque-amount" />
        <SelectField label="Cari" value={form.customer_id || ""}
                     onChange={(v) => setForm({ ...form, customer_id: v })}
                     options={[{ value: "", label: "—" }, ...customers.map((c) => ({ value: c.id, label: c.name }))]} />
        <SelectField label="Yön" value={form.direction}
                     onChange={(v) => setForm({ ...form, direction: v })}
                     options={[{ value: "received", label: "Alınan" }, { value: "given", label: "Verilen" }]} />
        <InputField label="Durum (Çekler sayfasındaki işlemlerle değişir)" value={form.status} disabled onChange={() => {}} />
      </div>
      <InputField label="Not" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
    </div>
  );
}

function PurchaseView({ data, accounts }) {
  const products = (data.items || []).filter((x) => !x.is_service);
  const services = (data.items || []).filter((x) => x.is_service);
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">Alım Faturası · {data.invoice_no || "-"}</h1>
        <span className={`type-tag ${data.status === "Ödendi" ? "corporate" : "individual"}`}>{data.status}</span>
      </div>
      <div className="view-grid">
        <Row label="Tedarikçi" testid="detail-customer">{data.supplier_name}</Row>
        <Row label="Tarih">{humanDate(data.date)}</Row>
        <Row label="Vade">{humanDate(data.due_date)}</Row>
        {data.paid_account_id && <Row label="Ödeme hesabı">{accounts.find((a) => a.id === data.paid_account_id)?.name || "—"}</Row>}
      </div>
      {!!(products.length + services.length) && (
        <table className="view-table" style={{ marginTop: 18 }}>
          <thead><tr><th>Kalem</th><th>Tür</th><th>Miktar</th><th className="amount">Birim</th><th className="amount">KDV</th><th className="amount">Tutar</th></tr></thead>
          <tbody>
            {[...products, ...services].map((it, i) => (
              <tr key={i}>
                <td><strong>{it.name}</strong><small>{it.code} {it.description && `· ${it.description}`}</small>{it.extra_note && <small style={{ display: "block", whiteSpace: "pre-wrap" }}>{it.extra_note}</small>}</td>
                <td>{it.is_service ? "Hizmet" : "Ürün"}</td>
                <td>{it.qty}</td>
                <td className="amount">{money(it.unit_price)}</td>
                <td className="amount">%{it.kdv}</td>
                <td className="amount">{money((Number(it.qty) || 0) * (Number(it.unit_price) || 0) * (1 + (Number(it.kdv) || 0) / 100))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="invoice-totals" style={{ marginTop: 18 }}>
        <div><span>Ara toplam</span><strong>{money(data.subtotal)}</strong></div>
        <div><span>KDV</span><strong>{money(data.kdv_total)}</strong></div>
        <div className="grand"><span>Toplam</span><strong data-testid="detail-total">{money(data.total)}</strong></div>
      </div>
      {data.note && <p className="modal-sub" style={{ marginTop: 14 }}><strong>Not:</strong> {data.note}</p>}
    </>
  );
}
function PurchaseEdit({ form, setForm, customers = [] }) {
  return (
    <div className="edit-form" data-testid="purchase-edit-form">
      <HideDailyToggle form={form} setForm={setForm} testid="purchase-edit-form-hide-daily" />
      <div className="form-grid two">
        <CustomerCombobox customers={customers} selectedId={form.supplier_id} selectedName={form.supplier_name}
                          label="Tedarikçi" testid="edit-supplier"
                          onSelect={(c) => setForm({ ...form, supplier_id: c.id, supplier_name: c.name })} />
        <InputField label="Fatura no" value={form.invoice_no} onChange={(v) => setForm({ ...form, invoice_no: v })} />
        <InputField label="Tarih" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
        <InputField label="Vade" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
        <SelectField label="Durum" value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                     options={["Ödenecek", "Ödendi", "İptal"]} />
      </div>
      <LineEditor rows={form.products || []} label="Ürün kalemleri" onChange={(rows) => setForm({ ...form, products: rows })} />
      <LineEditor rows={form.services || []} label="Hizmet kalemleri" withCode={false} onChange={(rows) => setForm({ ...form, services: rows })} />
      <InputField label="Not" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
    </div>
  );
}

function RecordView({ data, customers, accounts }) {
  const custName = (cid) => customers.find((c) => c.id === cid)?.name || "—";
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">{data.type === "income" ? "Gelir" : "Gider"} · {money(data.amount)}</h1>
        <span className={`type-tag ${data.type === "income" ? "corporate" : "individual"}`}>{data.type === "income" ? "Gelir" : "Gider"}</span>
      </div>
      <div className="view-grid">
        <Row label="Tarih">{humanDate(data.date)}</Row>
        <Row label="Vade">{humanDate(data.due_date)}</Row>
        <Row label="Durum">{data.status}</Row>
        <Row label="Kategori">{data.category || "—"}</Row>
        <Row label="Tutar" testid="detail-amount">{money(data.amount)}</Row>
        {data.customer_id && <Row label="Cari" testid="detail-customer">{custName(data.customer_id)} <CustomerInfoButton customerId={data.customer_id} testid="detail-customer-info" /></Row>}
        {data.account_id && <Row label="Hesap">{accounts.find((a) => a.id === data.account_id)?.name || "—"}</Row>}
      </div>
      {data.description && <p className="modal-sub" style={{ marginTop: 14 }}><strong>Açıklama:</strong> {data.description}</p>}
    </>
  );
}
function RecordEdit({ form, setForm, customers, accounts }) {
  return (
    <div className="edit-form" data-testid="record-edit-form">
      <div className="form-grid two">
        <SelectField label="Tür" value={form.type} onChange={(v) => setForm({ ...form, type: v })}
                     options={[{ value: "income", label: "Gelir" }, { value: "expense", label: "Gider" }]} />
        <InputField label="Tutar" money value={form.amount} testid="edit-record-amount"
                    onChange={(v) => setForm({ ...form, amount: v })} />
        <InputField label="Tarih" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
        <InputField label="Vade" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
        <SelectField label="Durum" value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                     options={["Ödendi", "Beklemede", "Kısmen Ödendi", "İptal"]} />
        <InputField label="Kategori" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
        <SelectField label="Cari" value={form.customer_id || ""}
                     onChange={(v) => setForm({ ...form, customer_id: v })}
                     options={[{ value: "", label: "—" }, ...customers.map((c) => ({ value: c.id, label: c.name }))]} />
        <SelectField label="Hesap" value={form.account_id || ""}
                     onChange={(v) => setForm({ ...form, account_id: v })}
                     options={[{ value: "", label: "—" }, ...accounts.map((a) => ({ value: a.id, label: a.name }))]} />
      </div>
      <InputField label="Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="edit-record-desc" />
    </div>
  );
}

function StockView({ data }) {
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">Stok · {data.code}</h1>
        <span className="type-tag corporate">{data.unit || "Adet"}</span>
      </div>
      <div className="view-grid">
        <Row label="Ad" testid="detail-stock-name">{data.name}</Row>
        <Row label="Kod">{data.code}</Row>
        <Row label="Barkod">{data.barcode || "—"}</Row>
        <Row label="Kategori" testid="detail-stock-category">{data.category || "—"}</Row>
        <Row label="Ana depo">{data.quantity}</Row>
        <Row label="Garanti deposu">{data.warranty_quantity || 0}</Row>
        <Row label="Birim fiyat">{money(data.price)}</Row>
        <Row label="KDV">%{data.kdv}</Row>
        <Row label="Kritik seviye">{data.min_level ?? "—"}</Row>
      </div>
      {data.description && <p className="modal-sub" style={{ marginTop: 14 }}>{data.description}</p>}
    </>
  );
}
function StockEdit({ form, setForm }) {
  return (
    <div className="edit-form" data-testid="stock-edit-form">
      <div className="form-grid two">
        <InputField label="Ad" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-stock-name" />
        <InputField label="Kod" value={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="edit-stock-code" />
        <InputField label="Barkod" value={form.barcode} onChange={(v) => setForm({ ...form, barcode: v })} testid="edit-stock-barcode" />
        <CategorySelect value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="edit-stock-category" />
        <InputField label="Birim" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} />
        <InputField label="Fiyat" money value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
        <SelectField label="KDV" value={form.kdv} onChange={(v) => setForm({ ...form, kdv: v })}
                     options={KDV_OPTS.map((k) => ({ value: k, label: `%${k}` }))} />
        <InputField label="Miktar" type="number" step="0.01" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} />
        <InputField label="Kritik seviye" type="number" step="0.01" value={form.min_level} onChange={(v) => setForm({ ...form, min_level: v })} />
      </div>
      <InputField label="Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
    </div>
  );
}

function ServiceView({ data }) {
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">Hizmet · {data.name}</h1>
        <span className="type-tag corporate">%{data.kdv}</span>
      </div>
      <div className="view-grid">
        <Row label="Ad" testid="detail-service-name">{data.name}</Row>
        <Row label="Kod">{data.code || "—"}</Row>
        <Row label="Birim fiyat">{money(data.price)}</Row>
      </div>
      {data.description && <p className="modal-sub" style={{ marginTop: 14 }}>{data.description}</p>}
      <ServiceMovements sid={data.id} />
    </>
  );
}

function ServiceMovements({ sid }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [mv, setMv] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams();
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    api.get(`/services/${sid}/movements?${p}`).then((r) => setMv(r.data)).catch(() => setMv({ movements: [], totals: {} }));
  }, [sid, from, to]);
  return (
    <div data-testid="service-movements" style={{ marginTop: 22 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", marginBottom: 12 }}>
        <h3 style={{ margin: 0, marginRight: "auto" }}>Satış hareketleri</h3>
        <label className="field"><span className="field-label">Başlangıç</span><input type="date" data-testid="svc-mov-from" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="field"><span className="field-label">Bitiş</span><input type="date" data-testid="svc-mov-to" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </div>
      {!mv ? <p className="muted">Yükleniyor…</p> : (
        <>
          <div className="view-grid">
            <Row label="Toplam adet" testid="svc-mov-qty">{mv.totals.total_quantity || 0}</Row>
            <Row label="Toplam kazanç (KDV hariç)" testid="svc-mov-amount">{money(mv.totals.total_amount || 0)}</Row>
            <Row label="Fatura sayısı">{mv.totals.invoice_count || 0}</Row>
            <Row label="Müşteri sayısı">{mv.totals.customer_count || 0}</Row>
          </div>
          <table className="view-table" data-testid="svc-mov-table" style={{ marginTop: 14 }}>
            <thead><tr><th>Tarih</th><th>Fatura</th><th>Cari</th><th>Plaka</th><th className="amount">Adet</th><th className="amount">Birim</th><th className="amount">Tutar</th></tr></thead>
            <tbody>
              {mv.movements.length === 0 && <tr><td colSpan="7" className="empty">Bu aralıkta satış yok.</td></tr>}
              {mv.movements.map((m, i) => (
                <tr key={`${m.invoice_id}-${i}`} className="clickable-row" data-testid={`svc-mov-row-${i}`}
                    {...rowLink(`/detay/fatura/${m.invoice_id}`)}>
                  <td>{humanDate(m.date)}</td>
                  <td><strong>{m.invoice_no || "—"}</strong><small>{m.invoice_type}</small></td>
                  <td>{m.customer_name || "—"}</td>
                  <td>{m.plate || "—"}</td>
                  <td className="amount">{m.quantity}</td>
                  <td className="amount">{money(m.unit_price)}</td>
                  <td className="amount"><strong>{money(m.line_total)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
function ServiceEdit({ form, setForm }) {
  return (
    <div className="edit-form" data-testid="service-edit-form">
      <div className="form-grid two">
        <InputField label="Ad" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-service-name" />
        <InputField label="Kod" value={form.code} onChange={(v) => setForm({ ...form, code: v })} />
        <InputField label="Fiyat" money value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
        <SelectField label="KDV" value={form.kdv} onChange={(v) => setForm({ ...form, kdv: v })}
                     options={KDV_OPTS.map((k) => ({ value: k, label: `%${k}` }))} />
      </div>
      <InputField label="Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
    </div>
  );
}

function AccountView({ data }) {
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">{data.type === "cash" ? "Kasa" : data.type === "pos" ? "POS Kasa" : "Banka hesabı"} · {data.name}</h1>
        <span className="type-tag corporate">{data.currency === "XAU" ? `Altın (${data.gold_unit === "adet" ? "adet" : "gr"})` : data.currency || "TRY"}</span>
      </div>
      <div className="view-grid">
        <Row label="Ad" testid="detail-account-name">{data.name}</Row>
        <Row label="Tür">{data.type === "cash" ? "Kasa" : data.type === "pos" ? "POS Kasa" : "Banka"}</Row>
        <Row label="Banka">{data.bank_name || "—"}</Row>
        <Row label="IBAN">{data.iban || "—"}</Row>
        <Row label="Para birimi">{data.currency === "XAU" ? `Altın (${data.gold_unit === "adet" ? "adet" : "gram"})` : data.currency || "TRY"}</Row>
        <Row label="Bakiye" testid="detail-account-balance">{moneyCur(data.balance || 0, data.currency, data.gold_unit)}</Row>
      </div>
      {data.note && <p className="modal-sub" style={{ marginTop: 14 }}>{data.note}</p>}
    </>
  );
}
function AccountEdit({ form, setForm }) {
  return (
    <div className="edit-form" data-testid="account-edit-form">
      <div className="form-grid two">
        <InputField label="Ad" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-account-name" />
        <SelectField label="Tür" value={form.type} onChange={(v) => setForm({ ...form, type: v })}
                     options={[{ value: "cash", label: "Kasa" }, { value: "bank", label: "Banka" }, { value: "pos", label: "POS Kasa" }]} />
        <InputField label="Banka" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
        <InputField label="IBAN" value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} />
        <SelectField label="Para birimi" value={form.currency || "TRY"} onChange={(v) => setForm({ ...form, currency: v })}
                     options={[{ value: "TRY", label: "TRY" }, { value: "USD", label: "USD" }, { value: "EUR", label: "EUR" }, { value: "XAU", label: "Altın" }]} />
        {form.currency === "XAU" && (
          <SelectField label="Altın birimi" value={form.gold_unit || "gr"} onChange={(v) => setForm({ ...form, gold_unit: v })} testid="edit-account-gold-unit"
                       options={[{ value: "gr", label: "Gram (gr)" }, { value: "adet", label: "Adet" }]} />
        )}
        {form.currency === "XAU" && (
          <SelectField label="Altın cinsi (TL karşılığı)" value={form.gold_type || "GRA"} onChange={(v) => setForm({ ...form, gold_type: v })}
                       testid="edit-account-gold-type" options={GOLD_TYPES} />
        )}
      </div>
      <InputField label="Not" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
    </div>
  );
}

function WorkOrderCancelBox({ data, user, onChanged }) {
  const cr = data.cancel_request;
  const isAdmin = user?.role === "admin";
  const err = (e) => alert(e.response?.data?.detail || "İşlem yapılamadı");
  const ask = () => {
    const reason = window.prompt("Bu iş neden iptal edildi? Sebebini yazın:");
    if (reason && reason.trim()) api.post(`/work-orders/${data.id}/cancel-request`, { reason }).then(onChanged).catch(err);
  };
  const reject = () => api.post(`/work-orders/${data.id}/cancel-reject`).then(onChanged).catch(err);
  const del = () => window.confirm("İş fişi kalıcı olarak silinsin mi?") &&
    api.delete(`/work-orders/${data.id}`).then(() => { alert("İş fişi silindi."); goBack(); }).catch(err);
  if (!cr) return (
    <button type="button" className="secondary-button" data-testid="wo-cancel-request" onClick={ask}>İptal olarak işaretle</button>
  );
  return (
    <div data-testid="wo-cancel-box" style={{ border: "1px solid #fca5a5", background: "#fef2f2", color: "#991b1b", borderRadius: 10, padding: "10px 14px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
      <span><strong>İptal talebi</strong> · {cr.by_name} · {humanDate(cr.at)}: {cr.reason}</span>
      {isAdmin && <button type="button" className="secondary-button" data-testid="wo-cancel-reject" onClick={reject}>Reddet</button>}
      {isAdmin && <button type="button" className="danger-button primary-button" style={{ background: "#dc2626" }} data-testid="wo-cancel-delete" onClick={del}>Fişi sil</button>}
    </div>
  );
}

function WorkOrderView({ data, canInvoice, user, onChanged }) {
  const [fullPhoto, setFullPhoto] = useState(false);
  return (
    <>
      <div className="detail-title">
        <h1 data-testid="detail-title">İş Fişi · {data.customer_name}</h1>
        <span className={`type-tag ${data.status === "işlendi" ? "corporate" : "individual"}`}>{data.status === "işlendi" ? "İşlendi" : "Beklemede"}</span>
      </div>
      {data.ruhsat_photo && (
        <div className="ruhsat-thumb" data-testid="ruhsat-thumb">
          <button type="button" className="photo-thumb-btn" title="Büyük görüntüle" data-testid="ruhsat-thumb-open" onClick={() => setFullPhoto(true)}>
            <img src={data.ruhsat_photo} alt="Ruhsat" />
          </button>
          <small>Ruhsat fotoğrafı (büyütmek için tıklayın)</small>
          {fullPhoto && <PhotoLightbox src={data.ruhsat_photo} onClose={() => setFullPhoto(false)} />}
        </div>
      )}
      <div className="view-grid">
        <Row label="Müşteri">{data.customer_name}</Row>
        {data.plate && <Row label="Plaka">{data.plate}</Row>}
        {data.chassis_no && <Row label="Şase">{data.chassis_no}</Row>}
        {data.owner_id_no && <Row label="TC / Vergi No">{data.owner_id_no}</Row>}
        {data.owner_address && <Row label={data.no_vehicle ? "Adres" : "Adres (ruhsat)"}>{data.owner_address}</Row>}
        {data.no_vehicle && <Row label="Tür">Araçsız kalem</Row>}
        {data.invoice_id && <Row label="Fatura"><a href={`/detay/fatura/${data.invoice_id}`} data-testid="wo-invoice-link">{data.invoice_no || "Faturayı aç"}</a></Row>}
        {data.invoice_removed && <Row label="Fatura durumu"><span style={{ color: "#dc2626", fontWeight: 700 }} data-testid="wo-invoice-removed">{data.invoice_removed.invoice_no} · {data.invoice_removed.reason}</span></Row>}
        {data.job && <Row label="Yapılan iş">{data.job}</Row>}
        <Row label="Personel">{data.created_by_name || "—"}</Row>
        <Row label="Oluşturulma">{humanDate(data.created_at)}</Row>
      </div>
      {data.items?.length > 0 && (
        <table className="view-table" style={{ marginTop: 18 }}>
          <thead><tr><th>Hizmet / Ürün</th><th>Miktar</th><th className="amount">Birim</th><th className="amount">Tutar</th></tr></thead>
          <tbody>
            {data.items.map((it, i) => (
              <tr key={i}>
                <td><strong>{it.name}</strong><small>{it.description || ""}</small></td>
                <td>{it.qty}</td>
                <td className="amount">{money(it.unit_price)}</td>
                <td className="amount">{money((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data.note && <p className="modal-sub" style={{ marginTop: 14 }}><strong>Notlar:</strong> {data.note}</p>}
      <div className="modal-actions" style={{ marginTop: 16, flexWrap: "wrap" }}>
        <WorkOrderCancelBox data={data} user={user} onChanged={onChanged} />
        <button type="button" className="secondary-button" onClick={() => window.print()}>Yazdır</button>
        {canInvoice && !data.invoice_id && <button type="button" className="primary-button" data-testid="wo-to-invoice"
                onClick={() => openInvoiceFromWorkOrders([data])}>
          Bu fişten fatura oluştur <ArrowUpRight size={14} />
        </button>}
      </div>
    </>
  );
}
